import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { PhoneInput } from '@components/core-ui/form-elements/PhoneInput';
import { Select } from '@components/core-ui/form-elements/Select';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { normalizeEmail } from '@helpers/normalizeEmail';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CanvasService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getApiCallErrorMessage } from 'app/http/getApiCallErrorMessage';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import { createFetchAdminOrganizationTeamsQueryFn } from 'app/queries/adminOrganizations/createFetchAdminOrganizationTeamsQueryFn';
import { createFetchAllOrganizationsQueryFn } from 'app/queries/organizations/createFetchAllOrganizationsQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import React, { useState } from 'react';

import { ButtonWrapper } from './styles/ButtonWrapper';
import { InputWrapper } from './styles/InputWrapper';

const LICENSE_OPTIONS = [
  { label: 'No license', value: 'NO_LICENSE' },
  { label: 'RS Pro: 30 days', value: 'RSP30' },
  { label: 'RS Pro: 60 days', value: 'RSP60' },
];

type TProps = {
  courseId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AddCourseStudentModal = ({ onCancel, onSuccess, courseId }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [closeAfterSubmit, setCloseAfterSubmit] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState<string | undefined>();
  const [team, setTeam] = useState<string | undefined>();
  const [license, setLicense] = useState('RSP60');

  const isValid = email && firstName && secondName && organization && team && license;

  const organizationsQuery = useQuery({
    queryKey: organizationsKeys.allList(),
    queryFn: createFetchAllOrganizationsQueryFn(call),
  });
  const organizations = (organizationsQuery.data ?? []).map((o) => ({ label: `${o.name} (${o.slug})`, value: o.id }));

  const orgTeamsQuery = useQuery({
    queryKey: adminOrganizationsKeys.teams(organization ?? ''),
    queryFn: createFetchAdminOrganizationTeamsQueryFn(call),
    enabled: !!organization,
  });
  useNotifyQueryError(organizationsQuery.error);
  useNotifyQueryError(orgTeamsQuery.error);
  const orgTeams = organization ? (orgTeamsQuery.data ?? []).map((o) => ({ label: o.name, value: o.id })) : [];

  const createStudentMutation = useMutation({
    mutationFn: (selection: { organization: string; team: string }) =>
      call(() =>
        CanvasService.createCourseStudent({
          courseId,
          requestBody: {
            email: normalizeEmail(email),
            name: firstName,
            lastName: secondName,
            phone,
            organization: selection.organization,
            team: selection.team,
            license,
          },
        }),
      ),
    onSuccess: () => {
      onSuccess();
      if (closeAfterSubmit) {
        closeModal();
      }
    },
    onError: (error) => {
      setErrorMessage(error instanceof ApiCallError ? getApiCallErrorMessage(error) : 'Something went wrong');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!isValid) {
      return;
    }
    createStudentMutation.mutate({ organization, team });
  };

  const handleOrganizationChange = (value: string | undefined) => {
    setOrganization(value);
    setTeam(undefined);
  };

  return (
    <ModalWrapper width="330px">
      {createStudentMutation.isPending && <AbsoluteSpinner overlay={true} />}
      <ModalTitle>Add new student</ModalTitle>
      <GenericPageError message={errorMessage} />
      <form onSubmit={submit}>
        <InputWrapper>
          <Input
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
            name="firstName"
            label="First name"
            type="text"
            value={firstName}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="lastName"
            label="Last name"
            type="text"
            value={secondName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSecondName(event.target.value)}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="email"
            label="Email"
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <PhoneInput
            onChange={(value) => setPhone(value)}
            defaultCountry="US"
            name="phone"
            placeholder="Phone number (e.g. +12223334455)"
            value={phone || ''}
            required={false}
          />
        </InputWrapper>
        <InputWrapper>
          <AutoCompleteSelect
            name="organization"
            label="Organization"
            value={organization}
            onChange={(_name, value) => handleOrganizationChange(value)}
            options={organizations}
          />
        </InputWrapper>
        <InputWrapper>
          <Select
            name="team"
            label="Team"
            value={team}
            disabled={!organization}
            onChange={(_name, value) => setTeam(value)}
            options={orgTeams}
          />
        </InputWrapper>
        <InputWrapper>
          <Select
            name="license"
            label="License"
            value={license}
            onChange={(_name, value) => setLicense(value)}
            options={LICENSE_OPTIONS}
          />
        </InputWrapper>
        <InputWrapper className="checkbox-wrapper">
          <CheckBox
            name="close-after-submit"
            checked={closeAfterSubmit}
            onChange={(value) => setCloseAfterSubmit(value)}
          />
          <label>Close this modal after student is added</label>
        </InputWrapper>

        <ButtonWrapper>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button type="submit" disabled={!isValid || createStudentMutation.isPending}>
            Add Student
          </Button>
        </ButtonWrapper>
      </form>
    </ModalWrapper>
  );
};
